import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { api } from '@/store/api';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  DropdownField,
  type DropdownOption,
} from '@gapartners/ui';
import type { CaseTimer, UpdateCaseTimerRequest } from '@/types/case';

const SECTION_SX = {
  p: 2,
  bgcolor: '#e3f2fd',
  borderRadius: 1,
  border: '1px solid #90caf9',
};

const SECTION_TITLE_SX = {
  mb: 1.5,
  display: 'block',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const ROW_SX = {
  py: 1,
  '&:not(:last-of-type)': { borderBottom: '1px solid #bbdefb' },
};

const FORM_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 1.5,
  mt: 1,
};

const LABEL_SX = {
  display: 'block',
  mb: 0.5,
  fontWeight: 600,
  fontSize: '0.8125rem',
};

const INPUT_SX = {
  boxSizing: 'border-box',
  width: '100%',
  minHeight: '36px',
  padding: '6px 12px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  bgcolor: 'background.paper',
};

const SELECT_FIELD_SX = {
  width: '100%',
  bgcolor: 'background.paper',
  '& .MuiOutlinedInput-root': {
    fontSize: '0.875rem',
    minHeight: '36px',
    '& .MuiOutlinedInput-input': { padding: '6px 12px' },
  },
  // A record always has a category, so there is nothing to clear. DropdownField's
  // types do not accept `disableClearable`.
  '& .MuiAutocomplete-clearIndicator': { display: 'none' },
};

interface TimerDraft {
  categoryId: number | null;
  /** `datetime-local` value, in the agent's time zone */
  start: string;
  minutes: string;
  notes: string;
}

/** ISO date to a `datetime-local` input value (local time, minute precision). */
function toLocalInputValue(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function toDraft(timer: CaseTimer): TimerDraft {
  return {
    categoryId: timer.categoryId,
    start: toLocalInputValue(timer.startDate),
    minutes: String(timer.minutes),
    notes: timer.notes,
  };
}

/** Only the fields the agent changed, so untouched values (e.g. seconds of the start) stay. */
function getChanges(
  timer: CaseTimer,
  draft: TimerDraft
): Omit<UpdateCaseTimerRequest, 'caseId' | 'timerId'> {
  const original = toDraft(timer);
  const changes: Omit<UpdateCaseTimerRequest, 'caseId' | 'timerId'> = {};
  if (draft.categoryId !== null && draft.categoryId !== original.categoryId) {
    changes.timeTrackingCategoryId = draft.categoryId;
  }
  if (draft.start !== original.start) {
    changes.startDate = new Date(draft.start).toISOString();
  }
  if (draft.minutes !== original.minutes) {
    changes.minutes = Number(draft.minutes);
  }
  if (draft.notes !== original.notes) {
    changes.notes = draft.notes;
  }
  return changes;
}

function isDraftValid(draft: TimerDraft): boolean {
  const minutes = Number(draft.minutes);
  return (
    draft.minutes.trim() !== '' &&
    Number.isInteger(minutes) &&
    minutes >= 0 &&
    !Number.isNaN(new Date(draft.start).getTime())
  );
}

interface CaseTimeTrackingSectionProps {
  caseId: number;
}

/**
 * ClientSpace time records of a case. Records are created with the case type's
 * default category when an agent's part of the call/chat ends; agents correct
 * the category, start, minutes and notes here.
 */
export function CaseTimeTrackingSection({
  caseId,
}: CaseTimeTrackingSectionProps) {
  const { t } = useTranslation();
  const { data: categoriesResponse } = api.useGetTimeTrackingCategoriesQuery();
  const {
    data: timersResponse,
    isLoading,
    isError,
    refetch,
  } = api.useGetCaseTimersQuery(caseId, { refetchOnMountOrArgChange: true });
  const [updateCaseTimer] = api.useUpdateCaseTimerMutation();
  const [editingTimerId, setEditingTimerId] = useState<number | null>(null);
  const [draft, setDraft] = useState<TimerDraft | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);

  const categoryOptions: DropdownOption[] = (
    categoriesResponse?.categories ?? []
  ).map(category => ({ id: category.id, label: category.name }));
  const timers = timersResponse?.timers ?? [];

  const startEditing = (timer: CaseTimer) => {
    setEditingTimerId(timer.id);
    setDraft(toDraft(timer));
    setHasSaveError(false);
  };

  const stopEditing = () => {
    setEditingTimerId(null);
    setDraft(null);
    setHasSaveError(false);
  };

  const handleSave = async (timer: CaseTimer) => {
    if (!draft) return;
    const changes = getChanges(timer, draft);
    if (Object.keys(changes).length === 0) {
      stopEditing();
      return;
    }
    setIsSaving(true);
    setHasSaveError(false);
    try {
      await updateCaseTimer({ caseId, timerId: timer.id, ...changes }).unwrap();
      await refetch();
      stopEditing();
    } catch {
      setHasSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const renderSummary = (timer: CaseTimer) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 1.5,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {timer.userName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {/* Same format as Call History */}
          {new Date(timer.startDate).toLocaleString()} ·{' '}
          {t('components.contactCenter.timeTrackingMinutes', {
            count: timer.minutes,
          })}{' '}
          · {timer.categoryName}
        </Typography>
        {timer.notes && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {timer.notes}
          </Typography>
        )}
      </Box>
      <Button
        size="small"
        variant="text"
        onClick={() => startEditing(timer)}
        disabled={editingTimerId !== null}
        sx={{ minWidth: 'auto', flexShrink: 0 }}
      >
        {t('common.edit')}
      </Button>
    </Box>
  );

  const renderForm = (timer: CaseTimer, currentDraft: TimerDraft) => {
    const fieldId = (name: string) => `case-timer-${timer.id}-${name}`;
    const update = (patch: Partial<TimerDraft>) =>
      setDraft(prev => (prev ? { ...prev, ...patch } : prev));
    const selected =
      categoryOptions.find(option => option.id === currentDraft.categoryId) ??
      null;

    return (
      <Box>
        <Typography variant="body2" fontWeight={600}>
          {timer.userName}
        </Typography>
        <Box sx={FORM_GRID_SX}>
          <Box>
            <Typography component="label" sx={LABEL_SX}>
              {t('components.contactCenter.timeTrackingCategory')}
            </Typography>
            <DropdownField
              label=""
              placeholder={timer.categoryName}
              multiple={false}
              options={categoryOptions}
              value={selected}
              onChange={(_e, val) => {
                if (Array.isArray(val) || !val || !('id' in val)) return;
                update({ categoryId: Number(val.id) });
              }}
              disabled={isSaving || categoryOptions.length === 0}
              getOptionId={o => o.id}
              getOptionLabel={o => o.label}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              filterOption={(o, input) =>
                String(o.label).toLowerCase().includes(input.toLowerCase())
              }
              sx={SELECT_FIELD_SX}
              fullWidth
            />
          </Box>
          <Box>
            <Box component="label" htmlFor={fieldId('minutes')} sx={LABEL_SX}>
              {t('components.contactCenter.timeTrackingMinutesSpent')}
            </Box>
            <Box
              component="input"
              id={fieldId('minutes')}
              type="number"
              min={0}
              step={1}
              value={currentDraft.minutes}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                update({ minutes: e.target.value })
              }
              disabled={isSaving}
              sx={INPUT_SX}
            />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Box component="label" htmlFor={fieldId('start')} sx={LABEL_SX}>
              {t('components.contactCenter.timeTrackingStart')}
            </Box>
            <Box
              component="input"
              id={fieldId('start')}
              type="datetime-local"
              value={currentDraft.start}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                update({ start: e.target.value })
              }
              disabled={isSaving}
              sx={INPUT_SX}
            />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Box component="label" htmlFor={fieldId('notes')} sx={LABEL_SX}>
              {t('components.contactCenter.timeTrackingNotes')}
            </Box>
            <Box
              component="textarea"
              id={fieldId('notes')}
              value={currentDraft.notes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                update({ notes: e.target.value })
              }
              disabled={isSaving}
              sx={{ ...INPUT_SX, minHeight: '60px', resize: 'vertical' }}
            />
          </Box>
        </Box>
        {hasSaveError && (
          <Typography
            variant="caption"
            color="error"
            display="block"
            sx={{ mt: 1 }}
          >
            {t('components.contactCenter.timeTrackingUpdateError')}
          </Typography>
        )}
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}
        >
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={stopEditing}
            disabled={isSaving}
          >
            {t('common.cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => void handleSave(timer)}
            loading={isSaving}
            disabled={isSaving || !isDraftValid(currentDraft)}
          >
            {t('common.save')}
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={SECTION_SX}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="primary.dark"
        sx={SECTION_TITLE_SX}
      >
        {t('components.contactCenter.timeTracking')}
      </Typography>
      {isLoading ? (
        <CircularProgress size={20} />
      ) : isError ? (
        <Typography variant="body2" color="error">
          {t('components.contactCenter.timeTrackingLoadError')}
        </Typography>
      ) : timers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('components.contactCenter.timeTrackingEmpty')}
        </Typography>
      ) : (
        timers.map(timer => (
          <Box
            key={timer.id}
            sx={ROW_SX}
            data-testid={`case-timer-${timer.id}`}
          >
            {editingTimerId === timer.id && draft
              ? renderForm(timer, draft)
              : renderSummary(timer)}
          </Box>
        ))
      )}
    </Box>
  );
}
